<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import {
  usersApi,
  type PitchLimitGrant,
  type PitchUsageSummary,
} from '@/api/users.api'
import { formatShortDate } from '@/utils/customers'

const props = defineProps<{
  userId: string
}>()

const usage = ref<PitchUsageSummary | null>(null)
const grants = ref<PitchLimitGrant[]>([])
const loading = ref(false)
const error = ref('')
const amount = ref(10)
const reason = ref('')
const saving = ref(false)
const saveError = ref('')
const revokingId = ref<string | null>(null)

const activeGrants = computed(() => grants.value.filter((g) => !g.revoked_at))
const revokedGrants = computed(() => grants.value.filter((g) => !!g.revoked_at))

function formatGrantDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return formatShortDate(value)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

async function loadGrants() {
  loading.value = true
  error.value = ''
  try {
    const res = await usersApi.listPitchGrants(props.userId)
    if (res.status !== 200) {
      error.value =
        (res.data as { detail?: string } | undefined)?.detail || 'Failed to load pitch grants'
      usage.value = null
      grants.value = []
      return
    }
    usage.value = res.data?.usage ?? null
    grants.value = res.data?.grants ?? []
  } catch (e: unknown) {
    error.value = e instanceof Error ? e.message : 'Failed to load pitch grants'
    usage.value = null
    grants.value = []
  } finally {
    loading.value = false
  }
}

async function addGrant() {
  const cleanedReason = reason.value.trim()
  const grantAmount = Number(amount.value)
  if (!cleanedReason || saving.value) return
  if (!Number.isInteger(grantAmount) || grantAmount <= 0) {
    saveError.value = 'Amount must be a positive whole number'
    return
  }

  saving.value = true
  saveError.value = ''
  try {
    const res = await usersApi.createPitchGrant(props.userId, grantAmount, cleanedReason)
    if (res.status !== 200 && res.status !== 201) {
      saveError.value =
        (res.data as { detail?: string } | undefined)?.detail || 'Failed to add pitches'
      return
    }
    if (res.data?.usage) usage.value = res.data.usage
    if (res.data?.grant) {
      grants.value = [res.data.grant, ...grants.value]
    } else {
      await loadGrants()
    }
    reason.value = ''
  } catch (e: unknown) {
    saveError.value = e instanceof Error ? e.message : 'Failed to add pitches'
  } finally {
    saving.value = false
  }
}

async function revokeGrant(grant: PitchLimitGrant) {
  if (grant.revoked_at || revokingId.value) return
  const ok = window.confirm(
    `Revoke +${grant.amount} pitches for this billing period? This cannot be undone.`,
  )
  if (!ok) return

  revokingId.value = grant.id
  saveError.value = ''
  try {
    const res = await usersApi.revokePitchGrant(props.userId, grant.id)
    if (res.status !== 200) {
      saveError.value =
        (res.data as { detail?: string } | undefined)?.detail || 'Failed to revoke grant'
      return
    }
    if (res.data?.usage) usage.value = res.data.usage
    if (res.data?.grant) {
      grants.value = grants.value.map((g) => (g.id === grant.id ? res.data!.grant : g))
    } else {
      await loadGrants()
    }
  } catch (e: unknown) {
    saveError.value = e instanceof Error ? e.message : 'Failed to revoke grant'
  } finally {
    revokingId.value = null
  }
}

watch(
  () => props.userId,
  () => {
    reason.value = ''
    amount.value = 10
    saveError.value = ''
    void loadGrants()
  },
)

onMounted(() => {
  void loadGrants()
})
</script>

<template>
  <section class="mt-5 border-t border-gray-100 pt-5">
    <div class="flex items-baseline justify-between gap-3">
      <h3 class="text-xs font-medium uppercase tracking-wide text-gray-400">
        Monthly pitch limit
      </h3>
      <span v-if="usage && !loading" class="text-xs tabular-nums text-gray-400">
        {{ usage.used }} / {{ usage.limit }} used
      </span>
    </div>

    <div v-if="usage && !loading" class="mt-3 grid grid-cols-2 gap-2 text-sm">
      <div class="rounded-lg border border-gray-200 p-2.5">
        <p class="text-[11px] font-medium uppercase tracking-wide text-gray-400">Base</p>
        <p class="mt-0.5 font-medium tabular-nums text-gray-900">{{ usage.base_limit }}</p>
      </div>
      <div class="rounded-lg border border-gray-200 p-2.5">
        <p class="text-[11px] font-medium uppercase tracking-wide text-gray-400">Bonus</p>
        <p class="mt-0.5 font-medium tabular-nums text-gray-900">+{{ usage.bonus }}</p>
      </div>
      <div class="rounded-lg border border-gray-200 p-2.5">
        <p class="text-[11px] font-medium uppercase tracking-wide text-gray-400">Remaining</p>
        <p class="mt-0.5 font-medium tabular-nums text-gray-900">{{ usage.remaining }}</p>
      </div>
      <div class="rounded-lg border border-gray-200 p-2.5">
        <p class="text-[11px] font-medium uppercase tracking-wide text-gray-400">Resets</p>
        <p class="mt-0.5 font-medium text-gray-900">{{ formatShortDate(usage.period_end) }}</p>
      </div>
    </div>
    <p v-if="usage && !loading" class="mt-2 text-xs text-gray-500">
      {{ usage.auto_generated ?? 0 }} auto-drafted pitch{{ usage.auto_generated === 1 ? '' : 'es' }} this period
      (free, not counted above)
    </p>

    <form class="mt-3 space-y-2" @submit.prevent="addGrant">
      <div class="flex gap-2">
        <input
          v-model.number="amount"
          type="number"
          min="1"
          step="1"
          class="w-24 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-forest focus:outline-none focus:ring-1 focus:ring-forest"
          aria-label="Bonus pitch amount"
        />
        <input
          v-model="reason"
          type="text"
          placeholder="Reason (required)"
          class="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-forest focus:outline-none focus:ring-1 focus:ring-forest"
        />
      </div>
      <div class="flex items-center justify-between gap-3">
        <p v-if="saveError" class="text-xs text-red-600">{{ saveError }}</p>
        <span v-else class="text-xs text-gray-400">Applies to the current billing period only</span>
        <button
          type="submit"
          class="shrink-0 rounded-lg bg-forest px-3 py-1.5 text-xs font-medium text-white hover:bg-forest/90 disabled:opacity-50"
          :disabled="saving || !reason.trim() || !amount || amount <= 0"
        >
          {{ saving ? 'Adding…' : 'Add pitches' }}
        </button>
      </div>
    </form>

    <p v-if="loading" class="mt-4 text-center text-sm text-gray-400">Loading pitch grants…</p>
    <p v-else-if="error" class="mt-4 text-center text-sm text-red-600">{{ error }}</p>
    <p v-else-if="!grants.length" class="mt-4 text-center text-sm text-gray-400">
      No bonus grants this period.
    </p>
    <ul v-else class="mt-4 space-y-3">
      <li
        v-for="grant in activeGrants"
        :key="grant.id"
        class="rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2.5"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span class="text-sm font-semibold tabular-nums text-gray-900">+{{ grant.amount }}</span>
              <time class="text-xs text-gray-500" :datetime="grant.created_at">
                {{ formatGrantDate(grant.created_at) }}
              </time>
              <span v-if="grant.author_email" class="truncate text-[11px] text-gray-400">
                {{ grant.author_email }}
              </span>
            </div>
            <p class="mt-1 whitespace-pre-wrap text-sm text-gray-800">{{ grant.reason }}</p>
          </div>
          <button
            type="button"
            class="shrink-0 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
            :disabled="revokingId === grant.id"
            @click="revokeGrant(grant)"
          >
            {{ revokingId === grant.id ? 'Revoking…' : 'Revoke' }}
          </button>
        </div>
      </li>
      <li
        v-for="grant in revokedGrants"
        :key="grant.id"
        class="rounded-lg border border-gray-100 bg-white px-3 py-2.5 opacity-60"
      >
        <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span class="text-sm font-semibold tabular-nums text-gray-500 line-through"
            >+{{ grant.amount }}</span
          >
          <span class="text-[11px] font-medium uppercase tracking-wide text-gray-400">Revoked</span>
          <time class="text-xs text-gray-400" :datetime="grant.created_at">
            {{ formatGrantDate(grant.created_at) }}
          </time>
        </div>
        <p class="mt-1 whitespace-pre-wrap text-sm text-gray-500">{{ grant.reason }}</p>
      </li>
    </ul>
  </section>
</template>
